import {
  isRoleGreaterThanOrEqual,
  isSpectator,
  MeetingContainerAPI,
  MeetingSessionOvenMediaReceiver,
  useMeetingAudioMixer,
  useOrderedMeetingSessions,
} from "@kalyzee/kast-web-sdk";
import { MeetingSessionStatus, UserRole } from "@kalyzee/kast-web-sdk/http";
import { delay, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { RTCStreamSenderParamsPreset } from "@kalyzee/kast-web-sdk/webrtc";
import { MeetingState, MeetingUpdateByIdResult } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";
import { LoadingView } from "../../../../components/utils/LoadingView";
import PauseView from "../../../../components/utils/PauseView";
import { getAudioContextForAudioMixer } from "../../../../helpers/audio";
import { joinMeetingFromPedagogy } from "../../../../helpers/meeting";
import { removePopupByGroup } from "../../../../helpers/popup";
import { toastError } from "../../../../helpers/toast";
import { useMeetingConfigurator, useMeetingContainerAPI, useMeetingSelector, useMultiMeetingAPI } from "../../../../hooks/meeting";
import { usePopulatedMeetingData } from "../../../../hooks/populatedData";
import { useSettings } from "../../../../hooks/settings";
import { useUser } from "../../../../store/session/hooks";
import { SessionMode } from "../../../../store/session/slices";
import { usePedagogyContext } from "../context";
import styles from "./meeting.module.css";
import { DevSoundIndicatorsBanner } from "../../../../components/utils/DevSoundIndicatorBanner";
import { DEFAULT_MAX_BITRATES } from "../../../../helpers/videos";

export const MeetingPedagogyPage = () => {
  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const pedagogyCtx = usePedagogyContext();
  const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);
  const [ready, setReady] = useState(false);

  const meetingId = pageContextRef.current?.params?.id;

  const multiMeetingAPI = useMultiMeetingAPI();
  const multiMeetingAPIRef = useRefAutoUpdate(multiMeetingAPI);
  const meetingContainerAPI = useMeetingContainerAPI(meetingId);

  const [meeting] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting);
  const [session, sessionRef] = useMeetingSelector(meetingContainerAPI, (state) => state.session);
  const orderedSessions = useOrderedMeetingSessions(meeting, (_, session) => {
    if (isSpectator(session.role)) return false;
    if (session.status !== MeetingSessionStatus.RUNNING) return false;
    if (session.user?.role && isRoleGreaterThanOrEqual(session.user.role, UserRole.ORGANIZER)) return false;
    return session.id !== sessionRef.current?.id;
  });

  const audioMixer = useMeetingAudioMixer(
    getAudioContextForAudioMixer(),
    meetingContainerAPI,
    true,
    useCallback(
      (_: MeetingContainerAPI, session: MeetingSessionOvenMediaReceiver) => {
        return pedagogyCtx?.audioValidator(session) ?? false;
      },
      [pedagogyCtx]
    ),
    settings.debug?.audio?.mixer
  );
  if (settings.debug?.audio?.mixer) (window as any).audioMixer = audioMixer;

  useMeetingConfigurator(meetingContainerAPI, {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.extra?.type === "camera") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.pedagogy?.webcam ?? DEFAULT_MAX_BITRATES.pedagogy.webcam;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_FACE,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio } },
                },
              },
            };
          }
          if (mediaSession.extra?.type === "screen") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.pedagogy?.screensharing ?? DEFAULT_MAX_BITRATES.pedagogy.screensharing;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_SCREENSHARING,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio } },
                },
              },
            };
          }
          return undefined;
        },
      },
      reception: {
        options: () => ({
          audio: false,
          video: { mode: "subscription", delayToDisableStream: 5000 },
        }),
      },
    },
  });

  const populatedData = usePopulatedMeetingData(meetingContainerAPI);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const updateMedias = useCallback(async () => {
    if (!meetingContainerAPI) return;
    const manager = meetingContainerAPI.emissionManager;
    if (session) {
      let camerasVideoIsEnabled = true;
      let screensVideoIsEnabled = true;
      let audioIsEnabled = true;
      session.medias?.forEach((m) => {
        if (m.extra?.type === "camera" || m.extra?.type === undefined) camerasVideoIsEnabled = camerasVideoIsEnabled && m.video.enabled;
        if (m.extra?.type === "screen" || m.extra?.type === undefined) screensVideoIsEnabled = screensVideoIsEnabled && m.video.enabled;
        audioIsEnabled = audioIsEnabled && m.audio.enabled;
      });
      if (!camerasVideoIsEnabled && manager.videoIsEnabled("cameras")) {
        await meetingContainerAPI.disableVideo({ direction: "outgoing", group: "cameras" });
      } else if (camerasVideoIsEnabled && !manager.videoIsEnabled("cameras")) {
        await meetingContainerAPI.enableVideo({ direction: "outgoing", group: "cameras" });
      }
      if (!screensVideoIsEnabled && manager.videoIsEnabled("screens")) {
        await meetingContainerAPI.disableVideo({ direction: "outgoing", group: "screens" });
      } else if (screensVideoIsEnabled && !manager.videoIsEnabled("screens")) {
        await meetingContainerAPI.enableVideo({ direction: "outgoing", group: "screens" });
      }

      if (!audioIsEnabled && manager.audioIsEnabled("cameras")) {
        await meetingContainerAPI.mute({ direction: "outgoing", group: "cameras" });
      } else if (audioIsEnabled && !manager.audioIsEnabled("cameras")) {
        await meetingContainerAPI.unmute({ direction: "outgoing", group: "cameras" });
      }

      if (!screensVideoIsEnabled && manager.audioIsEnabled("screens")) {
        await meetingContainerAPI.mute({ direction: "outgoing", group: "screens" });
      } else if (screensVideoIsEnabled && !manager.audioIsEnabled("screens")) {
        await meetingContainerAPI.unmute({ direction: "outgoing", group: "screens" });
      }
    }

    const state = meeting?.state;
    if (state === MeetingState.PAUSED) {
      audioMixer?.suspend().catch((err) => console.error("Error suspend audio : ", err));
    } else {
      audioMixer?.resume().catch((err) => console.error("Error resume audio : ", err));
    }
  }, [audioMixer, pedagogyCtxRef, meeting, meetingContainerAPI, session]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    const meetingId = pageContextRef.current?.params?.id;
    const meetingAPI = multiMeetingAPIRef.current?.all;
    if (meetingAPI && meetingId) {
      joinMeetingFromPedagogy(meetingAPI, meetingId, pedagogyCtxRef.current)
        .then((container) => {
          if (container) {
            setReady(true);
          } else {
            pageContextRef.current?.goTo(PageMasterMode.DASHBOARD);
          }
        })
        .catch((err) => {
          pageContextRef.current?.goTo(PageMasterMode.DASHBOARD);
        });
    } else {
      pageContextRef.current?.goTo(PageMasterMode.DASHBOARD);
    }

    const beforeGoToListener = pageContextRef.current?.addEventListener("onBeforeGoto", () => {
      removePopupByGroup("main"); // Clean popup asap
    });
    return () => {
      removePopupByGroup("main");
      beforeGoToListener?.flush();
      const ctx = pedagogyCtxRef.current;
      if (!ctx) return;
      const containers = ctx.containersExceptPedagogy.filter((c) => c.getMeetingId() === meetingId);
      ctx.scheduleDestroyContainer(containers, 5000); // destroy after 5 secondes
    };
  }, []);

  useEffect(() => {
    pedagogyCtxRef.current?.setOrderedSessions(orderedSessions);
  }, [orderedSessions]);

  useEffect(() => {
    updateMedias();
  }, [updateMedias]);

  if (!ready) return <LoadingView />;

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  return (
    <div className={styles.container}>
      <Outlet />
      <DevSoundIndicatorsBanner label={"Meeting"} meetingData={populatedData} canHearSession={pedagogyCtx?.audioValidator} />
    </div>
  );
};
